import React, { useEffect, useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LineChart, Line, Legend
} from "recharts";
import {
  FaBoxOpen, FaShoppingCart, FaHourglassHalf,
  FaRupeeSign, FaBox, FaUsers, FaSyncAlt,
  FaChartLine, FaExclamationTriangle
} from "react-icons/fa";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../common/ToastHandler";
import LoadingSpinner from "../Orders/LoadingSpinner";
import "./EcommerceDashboard.css";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(amount || 0));

const safeArray = (value) => Array.isArray(value) ? value : [];

const getErrorMessage = (err, fallback) => {
  const raw = err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
  return String(raw || fallback).replace(/\s*\(id=[^)]+\)/g, "");
};

const StatCard = ({ icon: Icon, title, value, bgColor, iconColor, helper }) => (
  <div className="ecom-stat-card">
    <div className="ecom-stat-card-inner">
      <div className="ecom-card-icon" style={{ backgroundColor: bgColor, color: iconColor }}>
        <Icon size={24} />
      </div>
      <div className="ecom-card-text">
        <p>{title}</p>
        <h3>{value}</h3>
        {helper && <span>{helper}</span>}
      </div>
    </div>
  </div>
);

const ChartContainer = ({ title, data, children }) => (
  <div className="ecom-chart-card">
    <div className="ecom-section-title">
      <h4>{title}</h4>
      <span>{data.length} points</span>
    </div>
    {data.length === 0 ? (
      <p className="ecom-no-data">No chart data available yet</p>
    ) : (
      <ResponsiveContainer width="100%" height={250}>
        {children}
      </ResponsiveContainer>
    )}
  </div>
);

const MiniTable = ({ title, subtitle, columns, rows, emptyText }) => (
  <div className="ecom-table-card">
    <div className="ecom-section-title">
      <div>
        <h4>{title}</h4>
        <p>{subtitle}</p>
      </div>
      <span>{rows.length} rows</span>
    </div>

    {rows.length === 0 ? (
      <div className="ecom-empty-table">{emptyText}</div>
    ) : (
      <div className="ecom-mini-table-wrap">
        <table className="ecom-mini-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column.header}>{column.header}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || row.name || index}>
                {columns.map((column) => (
                  <td key={column.header} data-label={column.header}>
                    {column.render ? column.render(row) : row[column.field]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const EcommerceDashboard = () => {
  const { Get } = useApi();
  const { handleToast } = useToastHandler();

  const [loading, setLoading] = useState(false);

  const [dashboardStats, setDashboardStats] = useState({
    totalProducts: 0, totalCustomers: 0,
    totalOrders: 0, pendingOrders: 0, completedOrders: 0, totalRevenue: 0,
  });
  const [dashboardError, setDashboardError] = useState("");
  const [chartData, setChartData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch custom storefront analytics.
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setDashboardError("");
        const res = await Get("analytics");

        const monthlyRevenue = safeArray(res.monthly_revenue);
        const monthlyOrders = safeArray(res.monthly_orders);
        const orderStatus = safeArray(res.order_status);
        const totalRevenue = monthlyRevenue.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
        const totalOrders = monthlyOrders.reduce((sum, item) => sum + Number(item.orders || 0), 0);
        const pendingOrders = orderStatus
          .filter((item) => String(item.status || "").toLowerCase() === "pending")
          .reduce((sum, item) => sum + Number(item.count || 0), 0);
        const completedOrders = orderStatus
          .filter((item) => String(item.status || "").toLowerCase() === "completed")
          .reduce((sum, item) => sum + Number(item.count || 0), 0);

        setDashboardStats({
          totalProducts: res.total_products ?? res.products_sold ?? 0,
          totalCustomers: res.total_customers ?? 0,
          totalOrders,
          pendingOrders,
          completedOrders,
          totalRevenue,
        });

        setRecentOrders(safeArray(res.recent_orders));

        const revenueByMonth = monthlyRevenue.reduce((acc, item) => {
          acc[item.month] = item.revenue ?? 0;
          return acc;
        }, {});
        const ordersByMonth = monthlyOrders.reduce((acc, item) => {
          acc[item.month] = item.orders ?? 0;
          return acc;
        }, {});
        const mergedChart = Array.from(new Set([
          ...Object.keys(ordersByMonth),
          ...Object.keys(revenueByMonth),
        ])).map((month) => ({
          day: month,
          orders: ordersByMonth[month] ?? 0,
          revenue: revenueByMonth[month] ?? 0,
        }));
        setChartData(mergedChart);
      } catch (err) {
        const message = getErrorMessage(err, "Failed to load dashboard data");
        setDashboardError(message);
        setChartData([]);
        setRecentOrders([]);
        handleToast({ type: "ERROR", message });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [refreshKey]);

  const statCards = useMemo(() => [
    { icon: FaBoxOpen, title: "Store Products", value: dashboardStats.totalProducts, helper: "Custom storefront products", bgColor: "#e0f2fe", iconColor: "#2563eb" },
    { icon: FaUsers, title: "Customers", value: dashboardStats.totalCustomers, helper: "Store customers", bgColor: "#dcfce7", iconColor: "#16a34a" },
    { icon: FaBox, title: "Completed Orders", value: dashboardStats.completedOrders, helper: "Fulfilled online orders", bgColor: "#eef2ff", iconColor: "#4f46e5" },
    { icon: FaShoppingCart, title: "Total Orders", value: dashboardStats.totalOrders, helper: "Custom website orders", bgColor: "#ede9fe", iconColor: "#7c3aed" },
    { icon: FaHourglassHalf, title: "Pending Orders", value: dashboardStats.pendingOrders, helper: "Waiting to complete", bgColor: "#fef3c7", iconColor: "#d97706" },
    { icon: FaRupeeSign, title: "Revenue", value: formatCurrency(dashboardStats.totalRevenue), helper: "Completed orders", bgColor: "#ccfbf1", iconColor: "#0f766e" },
  ], [dashboardStats]);

  return (
    <div className="responsive-box">
      <ToastContainer />
      <div className="ecom-dashboard-hero">
        <div>
          <span className="ecom-kicker"><FaChartLine /> E-Commerce Command Center</span>
          <h2>Custom Storefront Dashboard</h2>
          <p>Monitor store-specific products, customer orders, revenue and website activity from one clean workspace.</p>
        </div>
        <div className="ecom-hero-actions">
          <Link className="ecom-refresh-btn" to="/ecommerce/visitor-analytics"><FaUsers /> Visitor Analytics</Link>
          <button className="ecom-refresh-btn" onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>
            <FaSyncAlt className={loading ? "spinning" : ""} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="dashboard-loading"><LoadingSpinner /></div>
      ) : dashboardError ? (
        <div className="ecom-error-card">
          <FaExclamationTriangle />
          <div>
            <h3>Dashboard data could not be loaded</h3>
            <p>{dashboardError}</p>
            <button onClick={() => setRefreshKey((value) => value + 1)}>Try again</button>
          </div>
        </div>
      ) : (
        <>
          <div className="ecom-summary-grid">
            {statCards.map((card, index) => (
              <StatCard key={index} {...card} />
            ))}
          </div>

          <div className="ecom-charts-grid">
            <ChartContainer title="Orders Overview" data={chartData}>
              <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} barCategoryGap="20%">
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 13, fontWeight: 500 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 13 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
                  contentStyle={{ backgroundColor: "#fff", border: "none", borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "12px 16px" }}
                  labelStyle={{ fontWeight: 600, color: "#1e293b", marginBottom: "4px" }}
                />
                <Bar dataKey="orders" fill="url(#barGradient)" radius={[8, 8, 0, 0]} maxBarSize={60} />
              </BarChart>
            </ChartContainer>

            <ChartContainer title="Revenue Trend" data={chartData}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 12 }} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={3} />
              </LineChart>
            </ChartContainer>
          </div>

          <div className="ecom-tables-grid">
            <MiniTable
              title="Order Status"
              subtitle="Current order distribution"
              rows={statCards.slice(2, 5)}
              emptyText="No order activity found."
              columns={[
                { header: "Metric", field: "title" },
                { header: "Value", field: "value" },
                { header: "Info", field: "helper" },
              ]}
            />

            <MiniTable
              title="Recent Orders"
              subtitle="Latest customer activity"
              rows={recentOrders}
              emptyText="No recent orders found."
              columns={[
                { header: "Order", field: "order_id", render: (item) => `#${item.order_id || item.id || "-"}` },
                { header: "Customer", field: "customer", render: (item) => item.customer || "Guest" },
                { header: "Date", field: "date", render: (item) => item.date || "-" },
                { header: "Status", field: "status", render: (item) => <span className={`ecom-status ${item.status || "pending"}`}>{item.status || "pending"}</span> },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EcommerceDashboard;
